import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { contactsApi, chatsApi, teamsApi, lifeApi, numerologyApi } from './services/api';
import AuthPage from './components/Auth/AuthPage';
import Sidebar from './components/Sidebar/Sidebar';
import ContactList from './components/Contacts/ContactList';
import AIChatAgent from './components/AIChatAgent';
import Dashboard from './components/Dashboard/Dashboard';
import CertificationLanding from './components/Certification/CertificationLanding';
import CertificationDashboard from './components/Certification/CertificationDashboard';
import CertifiedProfile from './components/Certification/CertifiedProfile';
import PsychometricOnboarding from './components/Onboarding/PsychometricOnboarding';

// Main App Content (authenticated)
const AppContent = () => {
  const { user } = useAuth();
  
  // State
  const [contacts, setContacts] = useState([]);
  const [chats, setChats] = useState([]);
  const [teams, setTeams] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(() => {
    return localStorage.getItem('currentChatId') || null;
  });
  const [currentChat, setCurrentChat] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [lifeContext, setLifeContext] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [contactInsights, setContactInsights] = useState(null);
  const [userNumerology, setUserNumerology] = useState(null);

  // Fetch user numerology from backend (admin only)
  useEffect(() => {
    if (!user?.is_admin || !user?.dob) { setUserNumerology(null); return; }
    numerologyApi.calculate(user.dob)
      .then(({ numerology }) => setUserNumerology(numerology))
      .catch(() => setUserNumerology(null));
  }, [user?.is_admin, user?.dob]);

  // Load contacts
  const loadContacts = useCallback(async () => {
    try {
      setLoadingContacts(true);
      const { contacts } = await contactsApi.getAll();
      setContacts(contacts);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // Load chats
  const loadChats = useCallback(async () => {
    try {
      setLoadingChats(true);
      const { chats } = await chatsApi.getAll();
      setChats(chats);
    } catch (err) {
      console.error('Failed to load chats:', err);
    } finally {
      setLoadingChats(false);
    }
  }, []);

  // Load teams
  const loadTeams = useCallback(async () => {
    try {
      setLoadingTeams(true);
      const { teams } = await teamsApi.getAll();
      setTeams(teams);
    } catch (err) {
      console.error('Failed to load teams:', err);
    } finally {
      setLoadingTeams(false);
    }
  }, []);

  // Load life manager context
  const loadLifeContext = useCallback(async () => {
    try {
      const context = await lifeApi.getContext();
      setLifeContext(context);
    } catch (err) {
      console.error('Failed to load life context:', err);
    }
  }, []);

  // Persist currentChatId to localStorage
  useEffect(() => {
    if (currentChatId) {
      localStorage.setItem('currentChatId', currentChatId);
    } else {
      localStorage.removeItem('currentChatId');
    }
  }, [currentChatId]);

  // Initial load
  useEffect(() => {
    loadContacts();
    loadChats();
    loadTeams();
    loadLifeContext();
  }, [loadContacts, loadChats, loadTeams, loadLifeContext]);

  // Fetch anonymized insights for a contact (if linked to a user)
  const fetchContactInsights = useCallback(async (contactId) => {
    try {
      const data = await contactsApi.getInsights(contactId);
      if (data.available) {
        return data;
      }
    } catch (err) {
      console.error('Failed to fetch insights:', err.message);
    }
    return null;
  }, []);

  // Load chat messages when selecting a chat
  const selectChat = useCallback(async (chatId) => {
    if (!chatId) {
      setCurrentChatId(null);
      setCurrentChat(null);
      setMessages([]);
      setSelectedContact(null);
      setSelectedTeam(null);
      setTeamMembers([]);
      return;
    }

    try {
      const { chat, messages: loadedMessages } = await chatsApi.getOne(chatId);
      const formattedMessages = loadedMessages.map(m => ({ role: m.role, content: m.content, id: m.id, has_anatomy: m.has_anatomy }));
      
      // Set selected contact if chat has one
      const contact = chat.contact_id ? contacts.find(c => c.id === chat.contact_id) : null;
      
      // Clear team selection when loading a chat
      setSelectedTeam(null);
      setTeamMembers([]);
      setSelectedContact(contact);
      setContactInsights(null);
      setCurrentChat(chat);
      
      // Set messages and chatId together - messages MUST be set before or with chatId
      // because the key change will cause remount
      setMessages(formattedMessages);
      setCurrentChatId(chatId);

      // Fetch insights for the contact in background
      if (contact) {
        fetchContactInsights(contact.id).then(insights => {
          if (insights) setContactInsights(insights);
        });
      }
    } catch (err) {
      // Common after DB resets: localStorage has a stale chat id
      if (err?.message === 'Chat not found') {
        localStorage.removeItem('currentChatId');
        setCurrentChatId(null);
        setCurrentChat(null);
        setMessages([]);
        setSelectedContact(null);
        setSelectedTeam(null);
        setTeamMembers([]);
        setContactInsights(null);
        return;
      }
      console.error('Failed to load chat:', err);
    }
  }, [contacts, fetchContactInsights]);

  // Restore active chat on page refresh (after chats finish loading)
  const hasRestoredChat = React.useRef(false);
  useEffect(() => {
    if (hasRestoredChat.current) return;
    if (!loadingChats && currentChatId) {
      hasRestoredChat.current = true;
      selectChat(currentChatId);
    }
  }, [loadingChats, currentChatId, selectChat]);

  // Create new chat
  const createNewChat = useCallback(async (contactId = null) => {
    try {
      const contact = contactId ? contacts.find(c => c.id === contactId) : null;
      const title = contact ? `Chat about ${contact.name}` : 'New Chat';

      const { chat } = await chatsApi.create({
        title,
        contact_id: contactId
      });

      // Write to localStorage immediately — don't rely on useEffect timing
      localStorage.setItem('currentChatId', chat.id);

      setChats(prev => [chat, ...prev]);
      setCurrentChatId(chat.id);
      setCurrentChat(chat);
      setMessages([]);
      setSelectedContact(contact);

      return chat;
    } catch (err) {
      console.error('Failed to create chat:', err);
      return null;
    }
  }, [contacts]);

  // Delete chat
  const deleteChat = useCallback(async (chatId) => {
    try {
      await chatsApi.delete(chatId);
      setChats(prev => prev.filter(c => c.id !== chatId));
      
      setCurrentChatId(prev => {
        if (prev === chatId) {
          setCurrentChat(null);
          setMessages([]);
          setSelectedContact(null);
          setContactInsights(null);
          return null;
        }
        return prev;
      });
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  }, []);

  // Handle contact selection
  const handleSelectContact = useCallback(async (contact) => {
    // If already selected, don't reload
    if (contact && selectedContact?.id === contact.id) {
      return;
    }
    
    // Clear team selection and set contact
    setSelectedTeam(null);
    setTeamMembers([]);
    setSelectedContact(contact);
    setContactInsights(null);
    
    // Reset chat state (let it auto-create on first message)
    setMessages([]);
    setCurrentChat(null);
    setCurrentChatId(null);

    // Fetch insights in background (non-blocking)
    if (contact) {
      fetchContactInsights(contact.id).then(insights => {
        if (insights) setContactInsights(insights);
      });
    }
  }, [selectedContact, fetchContactInsights]);

  // Contact CRUD
  const handleAddContact = useCallback(async (contactData) => {
    const { contact } = await contactsApi.create(contactData);
    setContacts(prev => [...prev, contact].sort((a, b) => a.name.localeCompare(b.name)));
    return contact;
  }, []);

  const handleEditContact = useCallback(async (id, contactData) => {
    const { contact } = await contactsApi.update(id, contactData);
    setContacts(prev => prev.map(c => c.id === id ? contact : c));
    return contact;
  }, []);

  const handleDeleteContact = useCallback(async (id) => {
    await contactsApi.delete(id);
    setContacts(prev => prev.filter(c => c.id !== id));
    if (selectedContact?.id === id) {
      setSelectedContact(null);
    }
  }, [selectedContact]);

  // Team CRUD
  const handleAddTeam = useCallback(async (teamData) => {
    const { team, members } = await teamsApi.create(teamData);
    setTeams(prev => [team, ...prev]);
    return { team, members };
  }, []);

  const handleEditTeam = useCallback(async (id, teamData) => {
    const { team } = await teamsApi.update(id, teamData);
    setTeams(prev => prev.map(t => t.id === id ? { ...t, ...team } : t));
    return team;
  }, []);

  const handleDeleteTeam = useCallback(async (id) => {
    await teamsApi.delete(id);
    setTeams(prev => prev.filter(t => t.id !== id));
    if (selectedTeam?.id === id) {
      setSelectedTeam(null);
      setTeamMembers([]);
    }
  }, [selectedTeam]);

  const handleAddTeamMember = useCallback(async (teamId, contactId, role) => {
    const { member } = await teamsApi.addMember(teamId, contactId, role);
    setTeamMembers(prev => [...prev, member]);
    loadTeams(); // Refresh teams to update member count
    return member;
  }, [loadTeams]);

  const handleRemoveTeamMember = useCallback(async (teamId, contactId) => {
    await teamsApi.removeMember(teamId, contactId);
    setTeamMembers(prev => prev.filter(m => m.id !== contactId));
    loadTeams(); // Refresh teams to update member count
  }, [loadTeams]);

  // Handle team selection
  const handleSelectTeam = useCallback(async (team) => {
    if (!team) {
      setSelectedTeam(null);
      setTeamMembers([]);
      setSelectedContact(null);
      return;
    }

    // If already selected, don't reload
    if (selectedTeam?.id === team.id) {
      return;
    }

    try {
      const { team: fullTeam, members } = await teamsApi.getOne(team.id);
      
      // Clear contact selection and set team data first
      setSelectedContact(null);
      setTeamMembers(members);
      setSelectedTeam(fullTeam);
      setContactInsights(null);
      
      // Reset chat state for new team (don't create chat yet - let it auto-create on first message)
      setMessages([]);
      setCurrentChat(null);
      setCurrentChatId(null);

      // Fetch insights for all team members in parallel
      const insightsMap = {};
      const insightPromises = members.map(async (m) => {
        const data = await fetchContactInsights(m.id);
        if (data) insightsMap[m.id] = data;
      });
      await Promise.all(insightPromises);
      if (Object.keys(insightsMap).length > 0) {
        setContactInsights(insightsMap);
      }
    } catch (err) {
      console.error('Failed to load team:', err);
    }
  }, [selectedTeam, fetchContactInsights]);

  // Signal from AIChatAgent that a conversation turn completed (backend saves messages)
  const handleMessagesUpdate = useCallback((newChatId) => {
    loadChats();
    if (newChatId && newChatId !== currentChatId) {
      localStorage.setItem('currentChatId', newChatId);
      setCurrentChatId(newChatId);
    }
  }, [currentChatId]);

  // Build numerology data for chat
  const numerologyData = {
    dob: user?.dob,
    gender: user?.gender,
    mainGrid: userNumerology?.mainGrid || [],
    topGrid: userNumerology?.topGrid || [],
    bottomGrid: userNumerology?.bottomGrid || [],
    combinations: userNumerology?.combinations || []
  };

  // Fetch contact numerology from backend (admin only)
  const [contactNumerology, setContactNumerology] = useState(null);
  useEffect(() => {
    if (!user?.is_admin || !selectedContact?.dob) { setContactNumerology(null); return; }
    numerologyApi.calculate(selectedContact.dob)
      .then(({ numerology }) => setContactNumerology(numerology))
      .catch(() => setContactNumerology(null));
  }, [user?.is_admin, selectedContact?.id, selectedContact?.dob]);

  // Fetch team numerologies from backend (admin only)
  const [teamNumerologies, setTeamNumerologies] = useState(null);
  useEffect(() => {
    if (!user?.is_admin || !selectedTeam || teamMembers.length === 0) { setTeamNumerologies(null); return; }
    const entries = teamMembers.filter(m => m.dob).map(m => ({ id: m.id, dob: m.dob }));
    if (entries.length === 0) { setTeamNumerologies(null); return; }
    numerologyApi.calculateBatch(entries)
      .then(({ numerologies }) => setTeamNumerologies(numerologies))
      .catch(() => setTeamNumerologies(null));
  }, [user?.is_admin, selectedTeam, teamMembers]);

  // Close panels when a chat/contact/team is selected on mobile
  const handleMobileSelectChat = useCallback((chatId) => {
    selectChat(chatId);
    setSidebarOpen(false);
  }, [selectChat]);

  const handleMobileSelectContact = useCallback((contact) => {
    handleSelectContact(contact);
    setContactsOpen(false);
  }, [handleSelectContact]);

  const handleMobileSelectTeam = useCallback((team) => {
    handleSelectTeam(team);
    setContactsOpen(false);
  }, [handleSelectTeam]);

  return (
    <div className="app-layout">
      {/* Mobile Menu Buttons */}
      <button className="mobile-menu-btn mobile-sidebar-btn" onClick={() => setSidebarOpen(true)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12h18M3 6h18M3 18h18" />
        </svg>
      </button>
      <button className="mobile-menu-btn mobile-contacts-btn" onClick={() => setContactsOpen(true)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      </button>

      {/* Mobile Overlay */}
      {(sidebarOpen || contactsOpen) && (
        <div className="mobile-overlay visible" onClick={() => { setSidebarOpen(false); setContactsOpen(false); }} />
      )}

      {/* Left Sidebar - Chat History */}
      <Sidebar
        chats={chats}
        currentChatId={currentChatId}
        onSelectChat={handleMobileSelectChat}
        onNewChat={() => { createNewChat(selectedContact?.id); setSidebarOpen(false); }}
        onDeleteChat={deleteChat}
        loading={loadingChats}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Chat Area */}
      <main className="main-content">
        <AIChatAgent 
          embedded={true}
          numerologyData={numerologyData}
          messages={messages}
          onMessagesChange={handleMessagesUpdate}
          selectedContact={selectedContact}
          contactNumerology={contactNumerology}
          selectedTeam={selectedTeam}
          teamMembers={teamMembers}
          teamNumerologies={teamNumerologies}
          currentChatId={currentChatId}
          onNewChat={createNewChat}
          lifeContext={lifeContext}
          onLifeContextUpdate={loadLifeContext}
          contactInsights={contactInsights}
        />
      </main>

      {/* Right Panel - Contacts & Teams */}
      <ContactList
        contacts={contacts}
        teams={teams}
        selectedContactId={selectedContact?.id}
        selectedTeamId={selectedTeam?.id}
        onSelectContact={handleMobileSelectContact}
        onSelectTeam={handleMobileSelectTeam}
        onAddContact={handleAddContact}
        onEditContact={handleEditContact}
        onDeleteContact={handleDeleteContact}
        onAddTeam={handleAddTeam}
        onEditTeam={handleEditTeam}
        onDeleteTeam={handleDeleteTeam}
        onAddTeamMember={handleAddTeamMember}
        onRemoveTeamMember={handleRemoveTeamMember}
        loading={loadingContacts}
        loadingTeams={loadingTeams}
        isOpen={contactsOpen}
        onClose={() => setContactsOpen(false)}
      />
    </div>
  );
};

// Admin page layout — shared Sidebar + page content
const AdminPageLayout = ({ children }) => (
  <div className="app-layout">
    <Sidebar />
    <main className="main-content admin-page-content">{children}</main>
  </div>
);

// Authenticated routes wrapper
const AuthenticatedApp = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) return <AuthPage />;

  if (!user?.psychometric_completed) {
    return <PsychometricOnboarding />;
  }

  return (
    <Routes>
      <Route path="/dashboard" element={
        user?.is_admin ? <AdminPageLayout><Dashboard /></AdminPageLayout> : <Navigate to="/" replace />
      } />
      <Route path="/certification/dashboard" element={
        user?.is_admin ? <AdminPageLayout><CertificationDashboard /></AdminPageLayout> : <Navigate to="/" replace />
      } />
      <Route path="*" element={<AppContent />} />
    </Routes>
  );
};

// App wrapper with public + private routes
const App = () => {
  return (
    <Routes>
      <Route path="/certification" element={<CertificationLanding />} />
      <Route path="/certified/:id" element={<CertifiedProfile />} />
      <Route path="*" element={<AuthenticatedApp />} />
    </Routes>
  );
};

// Export with providers wrapper
const AppWithProviders = () => (
  <BrowserRouter>
    <ThemeProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default AppWithProviders;
